import { ComponentObservable, componentObserver } from '../../component-observer';
import { currentBreakpoint } from '../../utils/resize.utils';
import { FirstLevelMenuItem, getMenuItems, MenuType, SecondLevelMenuItem } from '../../api/menu-api';
import { Header, header } from '../header/header.module';
import { Param, readUrlParameter, writeToUrl } from '../../utils/url.utils';
import { createHtmlElementFromString } from '../../utils/html.utils';
import { CenterModule, CenterType } from '../../utils/center.module';
import { NewsArchiveDetailModule } from './news-archive-detail.module';
import { NewsArchiveList } from './news-archive-list.module';

export enum NewsView {
  NEWS_LIST,
  DETAIL
}

enum NewsTabId {
  TAB_ALL = 'tab-all'
}

/**
 * Initializes and renders the news center.
 */
class NewsArchive implements ComponentObservable, CenterModule {
  /**
   * Selector of news archive that must be initialized in javascript.
   */
  componentSelector = '.news-archive';

  /**
   * The translations for the news archive
   */
  translations: { [key: string]: string } = null;

  /**
   * Store the header menu items.
   */
  menuItems: FirstLevelMenuItem[] = [];

  /**
   * Store the last menu selection and search phrase.
   * This is the selection before the user searched using the search field. After
   * the user removes the search, this selection will be restored.
   */
  lastMenuSelection: {
    category: FirstLevelMenuItem;
    tab: SecondLevelMenuItem;
    searchPhrase: string;
  } = {
    category: null,
    tab: null,
    searchPhrase: null
  };

  /**
   * The last number of search results for the menu items.
   */
  totalResults: number;

  /**
   * If the header menu has been initialized
   */
  headerMenuInitialized = false;

  /**
   * The header component of the news archive.
   */
  newsArchiveHeader: Header = null;

  /**
   * The view of the news with filter etc.
   */
  newsList: NewsArchiveList = null;

  /**
   * The detail page when a news is selected.
   */
  newsArchiveDetail: NewsArchiveDetailModule = null;

  /**
   * The current view that is shown in the news archive.
   */
  currentView: NewsView = NewsView.NEWS_LIST;

  /**
   * Create the news archive. Fetches the menu items and initializes the sub components
   */
  createNewsArchive(root: HTMLElement): void {
    // add class to html element, for specific news archive styling
    document.documentElement.classList.add('html--news-archive');

    // Create header template
    this.createHeader(root);

    // and header menu, if not already rendered
    if (!this.headerMenuInitialized) {
      // otherwise render the header menu
      this.fetchHeaderMenuItems().then(async (menuItems) => {
        this.headerMenuInitialized = true;
        this.menuItems = menuItems;
        this.renderHeaderMenu(menuItems);
        this.createSubComponents(root);
      });
    }
  }

  /**
   * Creates the template for the header and setup translations.
   * @param root The news archive root element
   */
  createHeader(root: HTMLElement): void {
    // Create header from template and append it to the news archive
    const headerTemplate = createHtmlElementFromString(
      header.createTemplate(this.translations.headerTitle, this.translations.more, CenterType.NEWS_ARCHIVE)
    );
    root.append(headerTemplate);

    // Set translations
    headerTemplate.dataset.labelAllDevices = this.translations.allVentilators;
    headerTemplate.dataset.labelAll = this.translations.all;
    headerTemplate.dataset.labelMobileFilter = this.translations.filter;
    headerTemplate.dataset.labelMobileResetFilter = this.translations.mobileResetFilter;
    headerTemplate.dataset.labelMobileBackToAllCategoriesButton = this.translations.mobileBackToAllCategories;

    // If there was a ventilator ID in the URL, preselect it in the ventilator dropdown
    const ventilatorIdFromUrl = readUrlParameter(Param.VENTILATOR);
    if (ventilatorIdFromUrl) {
      headerTemplate.dataset.selectedDevice = ventilatorIdFromUrl;
    }
  }

  /**
   * Render the header menu with first and second level items.
   */
  renderHeaderMenu(menuItems: FirstLevelMenuItem[]): void {
    const headerElement = document.querySelector<HTMLElement>('.section--header');
    headerElement.dataset.tabs = JSON.stringify(menuItems);

    const subMenus = {};
    for (const item of menuItems) {
      subMenus[item.id] = item.secondaryCategories;
    }

    headerElement.dataset.subMenu = JSON.stringify(subMenus);
    header.initialize(headerElement).then(() => {
      this.setupNewsSearch(headerElement);
    });
  }

  /**
   * Initialize listeners for the news search input field and the cancel search button.
   */
  setupNewsSearch(headerElement: HTMLElement): void {
    const headerSearchField = headerElement.querySelector<HTMLInputElement>('.search-field__input');

    // if the URL contains a previous search phrase -> set it
    const searchPhraseFromUrl = readUrlParameter(Param.SEARCH_PHRASE);
    if (searchPhraseFromUrl) {
      headerSearchField.value = decodeURIComponent(searchPhraseFromUrl);

      // show the search field when a search phrase was given
      header.toggleSearch(headerElement, false);
    }

    // listen for changes in the input field (load results when the user submits the search)
    headerSearchField.addEventListener(
      'change',
      () => {
        const currentInput = headerSearchField.value;

        if (currentInput.length > 0) {
          // remove news id from the URL
          writeToUrl({ param: Param.SEARCH_PHRASE, value: encodeURIComponent(currentInput) }, [Param.NEWS_DETAIL_ID]);

          this.newsList.updateFilterWithNewSearchPhrase(currentInput, this.currentView === NewsView.NEWS_LIST);
          this.updateMobileMenuHeadingAndBackButton(headerElement, null, null, 'results');
        } else {
          this.removeSearchText();
          return;
        }

        if (this.currentView !== NewsView.NEWS_LIST) {
          // Remove news detail from url on tab click
          writeToUrl([], [Param.NEWS_DETAIL_ID], false, false);

          // if we are not on the news page, switch to it
          this.currentView = NewsView.NEWS_LIST;
          this.renderView();
        }
      },
      false
    );
    // reset the search when the user clicks the 'remove text' button in the search field
    const removeTextButton = headerElement.querySelector<HTMLElement>('#search-field-remove-text-button');
    removeTextButton.addEventListener(
      'click',
      () => {
        this.removeSearchText();
      },
      false
    );
  }

  /**
   * Reset the filters (sidebar). This function gets called by the header when the user selects
   * the 'all' tab of the tab bar.
   */
  resetFilters(): void {
    this.newsList?.resetFilters();
  }

  /**
   * Add a filter for a ventilator with the given ID. This function is called from the header,
   * when the user selects a ventilator from the dropdown.
   */
  updateFilterWithNewVentilator(itemId: string): void {
    this.newsList?.updateFilterWithNewVentilator(itemId);
  }

  /**
   * Remove the search phrase from the search input field. This clears the input field, removes the search filter,
   * and restores the menu and sub menu selection to the state before searching.
   */
  removeSearchText(): void {
    const parameters = [];
    const parametersToRemove = [Param.SEARCH_PHRASE];

    if (this.lastMenuSelection.category?.id) {
      parameters.push({ param: Param.CATEGORY, value: this.lastMenuSelection.category.id });
    } else {
      parametersToRemove.push(Param.CATEGORY);
      parametersToRemove.push(Param.TAB);
    }

    if (this.lastMenuSelection.tab?.id) {
      parameters.push({ param: Param.TAB, value: this.lastMenuSelection.tab.id });
    } else {
      parametersToRemove.push(Param.TAB);
    }

    // also remove it from the URL
    writeToUrl(parameters, parametersToRemove, false, true);
    window.location.reload();
  }

  /**
   * Update the search result tags for the first and second level menu items.
   * If they are not visible yet, they will get visible.
   */
  updateSearchResultCountTags(lastResultCount: number): void {
    const newsArchiveElement = document.querySelector('.news-archive');
    const allTab = newsArchiveElement.querySelector<HTMLElement>('ul[role="tablist"] a');

    // update the menu items counter tags
    // only set a number if it is not the all tab
    const counterTag = allTab.querySelector<HTMLElement>('.tag');

    // show the tag
    allTab.parentElement.classList.remove('selector-item--hidden');
    counterTag.classList.remove('tag--hidden');

    // update the number of results for the tab
    counterTag.innerText = lastResultCount.toString();

    // update the mobile header count tag
    const mobileHeaderCounterTag = newsArchiveElement.querySelector<HTMLElement>(
      '.navigation-mobile__selected-category .tag'
    );
    if (mobileHeaderCounterTag) {
      mobileHeaderCounterTag.innerText = `${lastResultCount}`;
      mobileHeaderCounterTag.classList.remove('tag--hidden');
    }
  }

  /**
   * Programmatically select a category and tab in the header menu.
   * @param headerElement: The header root element
   * @param categoryId: The id of the category that should get selected
   * @param tabId: The id of the tab that should get selected or null for no selection.
   * @param preventReload: Do not reload the news page, only update the header selection.
   */
  changeHeaderMenuSelection(
    headerElement: HTMLElement,
    categoryId: string,
    tabId: string,
    preventReload = false
  ): void {
    const tabList = headerElement.querySelector('ul[role="tablist"]');
    const breakpoint = currentBreakpoint();
    const isMobile = breakpoint.isMobilePortrait || breakpoint.isMobileLandscape;

    // deselect the currently active tab
    const selectedTab = tabList.querySelector('.selector-item--active');
    selectedTab?.classList.remove('selector-item--active');

    if (categoryId) {
      // set the category (first level item) that should get selected as active
      // querySelector with attribute selector is necessary because numbers as IDs are not valid CSS selectors
      tabList.querySelector(`[id='${categoryId}']`).parentElement.classList.add('selector-item--active');

      // create the sub menu for the respective first level option
      const headerSubNavigation = headerElement.querySelector<HTMLElement>('.header__sub-navigation');
      headerSubNavigation.removeAttribute('aria-hidden');
      this.createSubMenu(headerElement);
    }

    // update the URL parameters to reflect the current category/tab selection
    if (categoryId === NewsTabId.TAB_ALL) {
      writeToUrl({ param: Param.CATEGORY, value: 'all' });
      this.newsList.updateFirstLevelMenuSelection(null, false);

      if (isMobile) {
        // On mobile hide the menu if the 'all' category was selected -> directly go to the results
        header.hideMobileMenu(headerElement);
      }
    } else if (!categoryId) {
      writeToUrl([], [Param.CATEGORY], true, true);
      this.newsList.updateFirstLevelMenuSelection(null, false);
    } else {
      writeToUrl({ param: Param.CATEGORY, value: categoryId });
      this.newsList.updateFirstLevelMenuSelection(this.getFirstLevelMenuItemFromId(categoryId), !preventReload);
    }

    // if not on the overview page, reload the news list
    const reload = !preventReload;
    if (tabId !== null) {
      writeToUrl({ param: Param.TAB, value: tabId }, null, false, false);
      if (tabId !== 'all') {
        this.newsList.updateSecondLevelMenuSelection(this.getSecondLevelMenuItemFromId(tabId), reload);
      }
    } else {
      writeToUrl([], [Param.CATEGORY], true, true);
      this.newsList.updateSecondLevelMenuSelection(null, reload);
    }
  }

  /**
   * Reset the text input that the user typed into the search field.
   */
  resetNewsSearchInputField(): void {
    const headerElement = document.querySelector<HTMLElement>('.section--header');
    const headerSearchField = headerElement.querySelector<HTMLInputElement>('.search-field__input');
    headerSearchField.value = '';
  }

  /**
   * Renders the content depending on the current view.
   */
  renderView(): void {
    const newsRoot = document.querySelector<HTMLElement>(this.componentSelector);

    switch (this.currentView) {
      case NewsView.NEWS_LIST:
        this.newsArchiveDetail.reset();
        this.newsList.render(newsRoot);
        break;
      case NewsView.DETAIL:
        this.newsList.reset();
        this.newsArchiveDetail.reset();
        this.newsArchiveDetail.render(newsRoot);
        break;
    }
  }

  /**
   * Creates the tab navigation
   * @param headerElement The header element
   */
  createTabNavigation(headerElement: HTMLElement): void {
    const tabList = headerElement.querySelector('ul[role="tablist"]');

    if (headerElement.dataset.tabs) {
      const tabs = JSON.parse(headerElement.dataset.tabs);
      if (!tabs) {
        // eslint-disable-next-line no-console
        console.error('Tabs are missing');
      } else {
        // create the tab items
        let items = '';

        tabs.forEach((tab) => {
          items += `<li role="presentation" class="selector-item selector-item--active"><a role="tab"
          id="${tab.id}" tabindex="0">${tab.name}<span class="tag tag--counter tag--hidden"></span></a></li>`;
        });
        tabList.innerHTML = items;
      }
    }
  }

  /**
   * Creates the sub menu depending on the current tab
   * @param header
   */
  createSubMenu(header: HTMLElement): void {
    const subMenuContainer = header.querySelector<HTMLElement>('.sub-menu');

    const headerSearchField = header.querySelector<HTMLInputElement>('.search-field__input');
    const currentSearchPhrase = headerSearchField.value;

    if (header.dataset.subMenu) {
      subMenuContainer.innerHTML = `<li class="sub-menu__item sub-menu__item--active"><a id="sub-menu_all" tabindex="0">${this.translations.all}</a></li>`;

      if (currentSearchPhrase) {
        // If the user is currently searching, update the number of results for the sub menu items.
        this.updateSearchResultCountTags(this.totalResults);
      }
    }
  }

  /**
   * Update the menu heading and back button on mobile. Also shows/hides the filter on mobile.
   * @param header: Header root element.
   * @param category: The currently selected category.
   * @param tab: The currently selected tab or null if the 'all' tab has been selected.
   * @param level: If the mobile menu is currently showing the first or second level of the menu or the product results.
   */
  updateMobileMenuHeadingAndBackButton(
    header: HTMLElement,
    category: FirstLevelMenuItem,
    tab: SecondLevelMenuItem,
    level: 'menu' | 'sub-menu' | 'results' | 'detail'
  ): void {
    const currentTabLabel = header.querySelector<HTMLElement>('.navigation-mobile__selected-category');
    const categoryContainer = header.querySelector<HTMLElement>('.navigation-mobile__category-container');

    if (currentTabLabel) {
      const menuHeading = this.translations.all;
      const showCounter = level === 'results' && readUrlParameter(Param.SEARCH_PHRASE) !== null && category;

      // insert the heading before the counter tag
      const tabLabelContainsText = currentTabLabel.firstChild.nodeName.toLowerCase() !== 'span';
      if (tabLabelContainsText) {
        currentTabLabel.firstChild.nodeValue = menuHeading;
      } else {
        currentTabLabel.insertBefore(document.createTextNode(menuHeading), currentTabLabel.firstElementChild);
      }

      // hide or show the counter tag
      const counterTag = currentTabLabel.querySelector('.tag');
      counterTag.classList.toggle('tag--hidden', !showCounter);

      // and show the filter
      categoryContainer.classList.toggle('navigation-mobile__category-container--filter-visible', level === 'results');
    }
  }

  /**
   * Retrieve the header menu items.
   */
  fetchHeaderMenuItems(): Promise<FirstLevelMenuItem[]> {
    return getMenuItems(MenuType.MENU_NEWS);
  }

  /**
   * Get the first level menu item object from its ID. The menu items
   * are stored in this objects this.menuItems property.
   */
  getFirstLevelMenuItemFromId(menuItemId: string): FirstLevelMenuItem {
    let menuItem = null;
    for (const item of this.menuItems) {
      if (item.id === menuItemId) {
        menuItem = item;
      }
    }
    return menuItem;
  }

  /**
   * Get the second level menu item object from its ID. The menu items
   * are stored in this objects this.menuItems property.
   */
  getSecondLevelMenuItemFromId(menuItemId: string): SecondLevelMenuItem {
    let menuItem = null;
    for (const item of this.menuItems) {
      for (const secondLevelItem of item.secondaryCategories) {
        if (secondLevelItem.id === menuItemId) {
          menuItem = secondLevelItem;
        }
      }
    }
    return menuItem;
  }

  /**
   * Initialize all news archives on the current page.
   * @param observe Boolean if the NewsArchiveModule should listen for changes in the DOM and initialize dynamically added news archives.
   */
  initializeAll(observe: boolean): void {
    const allNewsArchives = document.querySelectorAll(this.componentSelector);
    for (let i = 0; i < allNewsArchives.length; i++) {
      this.initialize(allNewsArchives[i] as HTMLElement);
    }

    if (observe) {
      this.startListening();
    }
  }

  /**
   * Listen for changes in DOM and initialize e catalogs when new ones appear
   */
  startListening(): void {
    componentObserver.subscribeListener(this);
  }

  /**
   * Creates the objects for the components that are used in the news archive.
   * @param root The news archive element
   */
  createSubComponents(root: HTMLElement): void {
    const categoryIdFromUrl = readUrlParameter(Param.CATEGORY) || 'all';
    const tabIdFromUrl = readUrlParameter(Param.TAB);
    const searchPhraseFromUrl = readUrlParameter(Param.SEARCH_PHRASE);

    // Create header if not exists
    if (this.newsArchiveHeader === null) {
      const headerElement = root.querySelector<HTMLElement>('.section--header');
      this.newsArchiveHeader = header.prepareAndReturnHeader(true, this, true);
      this.createTabNavigation(headerElement);

      // when there was a category in the URL, use that one
      this.createSubMenu(headerElement);
    }

    // Create view of the news with filters
    if (this.newsList === null) {
      const pathsParameter = root.dataset.paths || '';
      const languageList = JSON.parse(root.dataset.newsLanguages);
      this.newsList = new NewsArchiveList(this.translations, pathsParameter, languageList, root.dataset.detailUrlNews);
      // Get the menu item object, so we can look up which filters we need
      const firstLevelItemId = categoryIdFromUrl || null;
      const firstLevelMenuItem = this.getFirstLevelMenuItemFromId(firstLevelItemId);
      this.newsList.updateFirstLevelMenuSelection(firstLevelMenuItem, false);

      if (firstLevelMenuItem) {
        // If there was a tab in the URL, select that one, otherwise select the 'all' tab.
        const secondLevelItemId = tabIdFromUrl || 'sub-menu_all';
        const secondLevelMenuItem = this.getSecondLevelMenuItemFromId(secondLevelItemId);
        this.newsList.updateSecondLevelMenuSelection(secondLevelMenuItem, false);
      }

      if (searchPhraseFromUrl) {
        this.newsList.updateFilterWithNewSearchPhrase(decodeURIComponent(searchPhraseFromUrl), false);
      }
    }

    // Create detail view
    if (this.newsArchiveDetail === null) {
      this.newsArchiveDetail = new NewsArchiveDetailModule(this.translations, root.dataset.detailUrlNews);

      // listen for url changes that are triggered by navigation in the news archive
      window.addEventListener('urlchange', () => {
        this.renderPageDependingOnUrlData(true);
      });

      // listen for url changes that are triggered by browser back/forward buttons
      window.addEventListener('popstate', () => {
        window.location.reload();
      });
    }

    // Check if url contains a news id
    this.renderPageDependingOnUrlData();

    // Render current view
    this.renderView();
  }

  /**
   * Checks if the url contains any news info and render the respective page.
   */
  renderPageDependingOnUrlData(reload?: boolean): void {
    const newsId = readUrlParameter(Param.NEWS_DETAIL_ID);
    const searchPhrase = readUrlParameter(Param.SEARCH_PHRASE);
    const category = readUrlParameter(Param.CATEGORY);
    if (newsId) {
      this.currentView = NewsView.DETAIL;
    } else if (category || searchPhrase) {
      this.currentView = NewsView.NEWS_LIST;
    }
    if (reload) {
      this.renderView();
    }
  }

  /**
   * Initialize the given news archive.
   * @param root The news archive root element that should be initialized
   */
  initialize(root: HTMLElement): void {
    componentObserver.markElementAsInitialized(this.componentSelector, root);

    if (!this.translations) {
      this.translations = JSON.parse(root.dataset.translations);
    }

    // render
    this.createNewsArchive(root);
  }

  /**
   * Called from the header when the print button was clicked.
   */
  printPage(): void {
    window.print();
  }
}

const newsArchiveModule = new NewsArchive();
export { newsArchiveModule as newsArchive };
